import isEmpty from "lodash/isEmpty";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuid } from "uuid";

import { commonActions } from "../../../common/redux/model";
import { AuroraClient } from "../../../utils/socket";
import { useMap } from "./map";
import { useMapStateUtilities } from "./hooks";
import { getSpatialInputFromContext } from "./utils/map-utils";
import { getMultiFilters } from "./utils/general-utils";

const channelId = uuid();

/**
 *
 * A custom hook that listens on the datasets array from the Context.
 * If there are no selected datasets, it clears the socket responses state.
 *
 */
export const useMapDataState = () => {
    const { datasets, dispatch } = useMap();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const selectDatasets = datasets.filter((d) => !!d.isSelected);

        if (isEmpty(selectDatasets)) {
            dispatch?.({
                type: "CLEAR_SOCKET_RESPONSES",
            });
        }
    }, [datasets.length]);
};

/**
 *
 * A custom hook that listens on the viewport and polygons from the Context, and sends requests to Velocity with the correct state.
 *
 */
export const useMapDataConnection = (
    getSockets: (dataSourceIds: string[]) => readonly AuroraClient[]
): { readonly channelId: string } => {
    const dispatch = useDispatch();

    const { viewport, polygons, datasets } = useMap();

    const multiFilters = getMultiFilters();

    const hasSelectedDatasets = !isEmpty(multiFilters);

    const { setLoading } = useMapStateUtilities();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (hasSelectedDatasets) {
            multiFilters.map((item) => {
                dispatch(
                    commonActions.setSearchStartTime({
                        datasetId: item.databaseId,
                        startTime: Date.now(),
                    })
                );
            });

            // 1. Get all data source ids with filters on them
            const dataSourceIds = multiFilters.map((d) => d.databaseId);

            // 2. Get all unique sockets for those datasource ids
            const sockets = getSockets(dataSourceIds);

            // 3. Send message to each socket, with multifilters only for dataset ids associated with that socket
            sockets.map((socket) => {
                setLoading({ data: true });

                const input = getSpatialInputFromContext({
                    channelId,
                    viewport,
                    polygons,
                    datasets: datasets.filter(
                        (dataset) =>
                            !!socket.dataSourceIds.includes(
                                dataset.dataSource.id
                            )
                    ),
                });

                socket.callBacks.sendSpatialDataQuery(input);
            });
        }
    }, [datasets, polygons, viewport]);

    return {
        channelId,
    };
};
